export type AnalyticsReportingMode = 'legacy' | 'v2' | 'v2_legacy';

export type Genie2ReportingMode = Exclude<AnalyticsReportingMode, 'legacy'>;

export const ANALYTICS_REPORTING_MODES = ['legacy', 'v2', 'v2_legacy'] as const;

export const GENIE2_REPORTING_MODES = ['v2', 'v2_legacy'] as const;

/** Staff UI for clients: all three modes. Partners keep legacy + v2 only. */
export const STAFF_CLIENT_ANALYTICS_REPORTING_MODE_OPTIONS = [
  'legacy',
  'v2',
  'v2_legacy',
] as const satisfies readonly AnalyticsReportingMode[];

export const STAFF_PARTNER_ANALYTICS_REPORTING_MODE_OPTIONS = [
  'legacy',
  'v2',
] as const satisfies readonly AnalyticsReportingMode[];

/** @deprecated Prefer STAFF_CLIENT_ / STAFF_PARTNER_ — kept for imports that expect the old name */
export const STAFF_ANALYTICS_REPORTING_MODE_OPTIONS = STAFF_CLIENT_ANALYTICS_REPORTING_MODE_OPTIONS;

export const ANALYTICS_REPORTING_MODE_LABELS: Record<AnalyticsReportingMode, string> = {
  legacy: 'Classic Genie',
  v2: 'New stats',
  v2_legacy: 'Migrated stats',
};

/** One-line staff helper shown in dropdowns and tooltips. */
export const ANALYTICS_REPORTING_MODE_DESCRIPTIONS: Record<AnalyticsReportingMode, string> = {
  legacy: 'Current UI. Genie 2.0 off.',
  v2: 'Genie 2.0 · new pipeline.',
  v2_legacy: 'Genie 2.0 · from Classic Genie.',
};

export const GENIE2_STAT_SET_HEADING = 'Pick a stat set';

export const ANALYTICS_REPORTING_MODE_SEARCH_PARAM = 'analyticsReportingMode';

type SessionWithReportingMode = {
  permissions?: Record<string, unknown>;
};

export function getAnalyticsReportingMode(
  sessionPlData?: SessionWithReportingMode,
): AnalyticsReportingMode {
  const mode = sessionPlData?.permissions?.analyticsReportingMode;
  if (mode === 'legacy' || mode === 'v2' || mode === 'v2_legacy') {
    return mode;
  }
  return 'legacy';
}

export function usesAnalyticsStatsUi(mode: AnalyticsReportingMode): boolean {
  return mode === 'v2' || mode === 'v2_legacy';
}

export function usesLegacyAnalyticsUi(mode: AnalyticsReportingMode): boolean {
  return mode === 'legacy';
}

export function isGenie2ReportingMode(
  mode: string | null | undefined,
): mode is Genie2ReportingMode {
  return mode === 'v2' || mode === 'v2_legacy';
}

/**
 * Parse the pre-seed search param for analytics-access.
 * Missing or invalid → null (chooser not completed). Never treat missing as v2.
 */
export function parseGenie2ReportingModeSearchParam(
  value: string | null | undefined,
): Genie2ReportingMode | null {
  if (isGenie2ReportingMode(value)) {
    return value;
  }
  return null;
}

/**
 * Send `{ analyticsReportingMode }` only while persisted is `legacy`.
 * Never emit `legacy`. Live accounts omit the param.
 */
export function staffConfigReportingModeQuery(
  persisted: AnalyticsReportingMode | null | undefined,
  selected: Genie2ReportingMode,
): { analyticsReportingMode: Genie2ReportingMode } | undefined {
  if (persisted !== 'legacy') {
    return undefined;
  }
  return { analyticsReportingMode: selected };
}

/** For future v2_legacy-specific API query params / headers — do not use as the request param name. */
export function analyticsStatsDataSource(
  mode: AnalyticsReportingMode,
): 'new' | 'legacy_import' | null {
  if (mode === 'v2') return 'new';
  if (mode === 'v2_legacy') return 'legacy_import';
  return null;
}

export function getAnalyticsReportingModeChangeMessage(
  from: AnalyticsReportingMode,
  to: AnalyticsReportingMode,
): string {
  if (from === to) return '';

  if (to === 'legacy') {
    return 'Return this client to Classic Genie? Genie 2.0 will close for them.';
  }

  if (from === 'legacy' && to === 'v2') {
    return 'Go-live with Genie 2.0 new stats?';
  }

  if (from === 'legacy' && to === 'v2_legacy') {
    return 'Go-live with Genie 2.0 migrated stats?';
  }

  if (usesAnalyticsStatsUi(from) && usesAnalyticsStatsUi(to)) {
    return 'Switch stat sets? This clears the other set’s access, templates, and schedules for this client.';
  }

  return `Switch this client to ${ANALYTICS_REPORTING_MODE_LABELS[to]}?`;
}

export type ReportingModeInvalidated = {
  limitViews: number;
  customViews: number;
  schedules: number;
};

export function formatReportingModeInvalidatedMessage(
  invalidated: ReportingModeInvalidated,
): string | null {
  const parts: string[] = [];
  if (invalidated.limitViews > 0) {
    parts.push(
      `${invalidated.limitViews} column-access config${invalidated.limitViews === 1 ? '' : 's'}`,
    );
  }
  if (invalidated.customViews > 0) {
    parts.push(
      `${invalidated.customViews} saved report${invalidated.customViews === 1 ? '' : 's'}`,
    );
  }
  if (invalidated.schedules > 0) {
    parts.push(`${invalidated.schedules} schedule${invalidated.schedules === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) {
    return null;
  }
  return `Cleared ${parts.join(', ')} for the other stat set. This client only.`;
}
